import React, { useState, useCallback, useEffect, useRef } from "react"
import { ReactComponent as EmailSentIcon } from '../../assets/icons/email-sent.svg';
import { Button, Form, Input, Space, Typography, notification } from "antd";
import { motion } from 'framer-motion';
import { useHistory } from "react-router-dom";
import ajaxService from "../../services/ajax-service";
import { CloseCircleOutlined } from "@ant-design/icons";
import AuthLayout from './AuthLayout';

const { Title, Text } = Typography

const VerifyOtp = () => {
    const history = useHistory()
    const inputRefs = useRef([]);

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const showError = useCallback((message) => {
        notification.open({
            message,
            icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} />,
            duration: 3,
        });
    }, []);

    const onFinish = useCallback(async (values) => {
        try {
            setLoading(true);
            const email = localStorage.getItem('forgotPasswordEmail');
            if (!email) {
                showError('Email not found. Please request OTP again.');
                return;
            }
            const otp = Object.values(values).join(''); // Combine otp digits
            const data = { email, otp };
            const response = await ajaxService.post('/verify_otp', data);

            if (response.success) {
                history.push({ pathname: '/reset-password' });
            } else {
                showError(response.data?.message || "The provided OTP does not match the expected value.");
            }
        } catch (error) {
            console.error('OTP verification error:', error);
            showError('An unexpected error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [history, showError]);

    const onFinishFailed = useCallback((errorInfo) => {
        console.log('Form validation failed:', errorInfo);
        if (errorInfo.errorFields.length > 0) {
            showError('Please check your input and try again.');
        }
    }, [showError]);

    // Handle OTP input changes
    const handleOtpChange = (e, index) => {
        const value = e.target.value.replace(/\D/g, ''); // Only allow digits
        if (value.length > 1) {
            // If more than one digit, fill next inputs
            let remaining = value;
            for (let i = index; i < 4 && remaining; i++) {
                const nextValue = remaining[0];
                form.setFieldsValue({ [`otp_${i}`]: nextValue });
                remaining = remaining.slice(1);
                if (inputRefs.current[i]) {
                    inputRefs.current[i].focus();
                }
            }
        } else {
            form.setFieldsValue({ [`otp_${index}`]: value });
        }
        // Auto-focus next
        if (value && index < 3) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    // Handle key down for backspace
    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !e.target.value && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === 'ArrowRight' && index < 3) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    // Handle paste
    const handlePaste = (e, index) => {
        e.preventDefault();
        const paste = e.clipboardData.getData('text').replace(/\D/g, '');
        let remaining = paste;
        for (let i = index; i < 4 && remaining; i++) {
            const digit = remaining[0];
            form.setFieldsValue({ [`otp_${i}`]: digit });
            remaining = remaining.slice(1);
            if (inputRefs.current[i]) {
                inputRefs.current[i].focus();
            }
        }
    };

    const headerRenderer = (props) => {
        const { iconSize, spacing, titleSize, textSize, isSmallHeight } = props;
        return (
            <>
                <EmailSentIcon style={{ 
                    height: iconSize, 
                    margin: `0 auto ${spacing}`,
                    display: 'block',
                }} />
                <Title
                    level={3}
                    style={{ 
                        margin: `${spacing} 0 0 0`, 
                        color: 'var(--text-white)',
                        fontSize: titleSize,
                        fontWeight: 700,
                    }}
                >
                    Verification OTP Code
                </Title>
                <Text 
                    style={{ 
                        color: 'var(--text-gray-light)', 
                        fontSize: textSize,
                        display: isSmallHeight ? 'none' : 'block',
                        marginTop: spacing,
                    }}
                >
                    We have sent the verification code to your email. Please check your inbox to complete the verification process.
                </Text>
            </>
        );
    };

    const formContent = (props) => {
        const { formItemStyle, buttonItemStyle, textSize, formSize, spacing, fieldGap, otpInputSize, otpInputFont, buttonHeight, buttonFont } = props;
        return (
            <Form
                form={form}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                layout="vertical"
                size={formSize}
                autoComplete="on"
                aria-label="Verify OTP form"
                style={{ margin: 0 }}
            >
                <Form.Item
                    style={{ marginBottom: fieldGap }}
                >
                    <Space direction="horizontal" size={4} style={{ justifyContent: 'center', width: '100%' }}>
                        {Array.from({ length: 4 }, (_, index) => (
                            <Form.Item
                                key={index}
                                name={`otp_${index}`}
                                rules={[{ required: true, message: `OTP digit ${index + 1} is required!` }]}
                                noStyle
                            >
                                <Input
                                    ref={(el) => (inputRefs.current[index] = el)}
                                    maxLength={1}
                                    type="text"
                                    pattern="[0-9]*"
                                    inputMode="numeric"
                                    onChange={(e) => handleOtpChange(e, index)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    onPaste={(e) => handlePaste(e, index)}
                                    style={{
                                        width: otpInputSize,
                                        height: otpInputSize,
                                        borderRadius: 'var(--radius-md)',
                                        background: 'var(--dark-bg-card)',
                                        border: '2px solid var(--border-light)',
                                        color: 'var(--text-white)',
                                        fontSize: otpInputFont,
                                        fontWeight: 600,
                                        textAlign: 'center',
                                        margin: '0 2px',
                                        transition: 'border-color 0.2s ease',
                                    }}
                                />
                            </Form.Item>
                        ))}
                    </Space>
                </Form.Item>

                <Form.Item style={{...buttonItemStyle, marginTop: spacing }}>
                    <Button 
                        loading={loading} 
                        type="primary" 
                        htmlType="submit" 
                        block
                        size={formSize}
                        style={{ 
                            background: 'var(--gradient-primary)',
                            border: 'none',
                            borderRadius: 'var(--radius-lg)',
                            height: buttonHeight,
                            fontWeight: 600,
                            fontSize: buttonFont,
                            marginTop:spacing,
                            boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.3)',
                        }}
                        aria-label={loading ? 'Verifying OTP' : 'Verify OTP'}
                    >
                        {loading ? 'Verifying...' : 'Verify OTP'}
                    </Button>
                </Form.Item>
            </Form>
        );
    };

    useEffect(() => {
        // Auto-focus first input
        inputRefs.current[0]?.focus();
    }, []);

    return (
        <AuthLayout
            headerRenderer={headerRenderer}
            formContent={formContent}
            onError={showError}
        />
    );
}

export default React.memo(VerifyOtp)